import React from 'react';
import { Skeleton } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import './style.scss';

export type StatCardTone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'violet';
export type StatCardVariant = 'classic' | 'bold' | 'glass';

export interface StatCardProps {
  title: React.ReactNode;
  value: React.ReactNode;
  icon?: React.ReactNode;
  tone?: StatCardTone;
  variant?: StatCardVariant;
  /** Stagger entrance delay in seconds */
  delay?: number;
  /** Optional trend percentage. Positive => up/green, negative => down/red. */
  trend?: number;
  trendLabel?: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}

/**
 * Reusable animated KPI card. Presentation only — no data fetching.
 */
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  tone = 'primary',
  variant = 'bold',
  delay = 0,
  trend,
  trendLabel,
  loading = false,
  onClick,
  className,
}) => {
  const hasTrend = typeof trend === 'number' && !Number.isNaN(trend);
  const trendPositive = hasTrend && (trend as number) >= 0;

  const rootClass = [
    'ui-stat-card',
    `ui-stat-card--${tone}`,
    `ui-stat-card--${variant}`,
    onClick ? 'ui-stat-card--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.div
      className={rootClass}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{
        y: -6,
        scale: 1.02,
        transition: { duration: 0.25 },
      }}
      whileTap={onClick ? { scale: 0.98 } : undefined}
    >
      <span className="ui-stat-card__orb ui-stat-card__orb--1" aria-hidden="true" />
      <span className="ui-stat-card__orb ui-stat-card__orb--2" aria-hidden="true" />
      <span className="ui-stat-card__accent" aria-hidden="true" />
      {loading ? (
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} />
      ) : (
        <div className="ui-stat-card__content">
          {icon ? <div className="ui-stat-card__icon">{icon}</div> : null}
          <div className="ui-stat-card__info">
            <div className="ui-stat-card__value">{value}</div>
            <div className="ui-stat-card__title">{title}</div>
            {hasTrend ? (
              <div
                className={`ui-stat-card__trend ${trendPositive ? 'is-positive' : 'is-negative'}`}
              >
                {trendPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                <span>
                  {Math.abs(trend as number)}%{trendLabel ? ` ${trendLabel}` : ''}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StatCard;
